import { useEffect, useRef } from 'react';
import type { BetSide, BetStatus, Phase } from '@platform/contracts';
import { resolveGameStoreHook } from '@core/gameengine/state/activeGameStore';
import { isSkyxNewRoute } from '@core/utils/gameRouteSlugs';

type BettingController = {
  resolveBetAction?: (side: BetSide) => void;
  cancelBet?: (side: BetSide) => void;
  cashout?: (side: BetSide) => void;
  getGameSlug?: () => string;
  getGameStateStore?: () => unknown;
};

type BetBoxLike = {
  status?: BetStatus;
  error?: string | null;
};

type BettingHotkeyStoreState = {
  phase?: Phase;
  bets?: Partial<Record<BetSide, BetBoxLike>>;
  autoPlaySettings?: Partial<Record<BetSide, unknown>>;
  isAutoPlayModalOpen?: boolean;
  stopAutoPlay?: (side: BetSide) => void;
  upBet?: (side: BetSide, patch: Record<string, unknown>) => void;
};

const BET_SIDES: BetSide[] = ['A', 'B'];

function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) {
    return false;
  }

  const tag = target.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') {
    return true;
  }

  if (target.isContentEditable) {
    return true;
  }

  return Boolean(target.closest('[contenteditable="true"]'));
}

function isBettingPhase(phase: Phase | undefined): boolean {
  return (
    phase === 'WAITING' ||
    phase === 'FLYING' ||
    phase === 'REVEAL' ||
    phase === 'FLEW_AWAY'
  );
}

function canCancelArmed(phase: Phase | undefined): boolean {
  return (
    phase === 'WAITING' ||
    phase === 'REVEAL' ||
    phase === 'FLEW_AWAY' ||
    phase === 'FLYING'
  );
}

function canCashout(
  box: BetBoxLike | undefined,
  phase: Phase | undefined
): boolean {
  return Boolean(
    box && box.status === 'placed' && !box.error && phase === 'FLYING'
  );
}

function stopAutoPlayForSide(
  store: BettingHotkeyStoreState,
  side: BetSide
): void {
  store.stopAutoPlay?.(side);
  store.upBet?.(side, { autoCashoutEnabled: false });
}

/**
 * Mirrors BetBox main-button actions for keyboard use.
 * Cashout wins when any side can cash out; otherwise place/cancel on preferred side.
 */
export function performBettingSpacebarAction(
  controller: BettingController,
  store: BettingHotkeyStoreState,
  preferredSide: BetSide = 'A'
): boolean {
  if (store.isAutoPlayModalOpen) {
    return false;
  }

  const phase = store.phase;
  let cashedOut = false;

  for (const side of BET_SIDES) {
    if (canCashout(store.bets?.[side], phase)) {
      controller.cashout?.(side);
      cashedOut = true;
    }
  }

  if (cashedOut) {
    return true;
  }

  const side = preferredSide;
  const box = store.bets?.[side];
  const status = box?.status;
  const isAutoPlayActive = store.autoPlaySettings?.[side] != null;

  switch (status) {
    case 'idle':
    case 'cashed':
      if (isAutoPlayActive) {
        stopAutoPlayForSide(store, side);
        return true;
      }
      if (!isBettingPhase(phase)) {
        return false;
      }
      controller.resolveBetAction?.(side);
      return true;

    case 'armed':
      if (!canCancelArmed(phase)) {
        return false;
      }
      if (isAutoPlayActive) {
        stopAutoPlayForSide(store, side);
      }
      controller.cancelBet?.(side);
      return true;

    case 'placed':
      if (phase !== 'WAITING' || box?.error) {
        return false;
      }
      if (isAutoPlayActive) {
        stopAutoPlayForSide(store, side);
      }
      controller.cancelBet?.(side);
      return true;

    default:
      return false;
  }
}

/**
 * Spacebar → cashout when possible, otherwise place/cancel on the preferred side.
 * Mount once (e.g. BetBox side A) so dual panels do not double-fire.
 *
 * Permanently disabled for skyx_new / sky-x in every jurisdiction.
 */
export function useBettingSpacebarShortcut(
  controller: BettingController | null | undefined,
  preferredSide: BetSide = 'A'
): void {
  const controllerRef = useRef(controller);
  controllerRef.current = controller;

  const preferredSideRef = useRef(preferredSide);
  preferredSideRef.current = preferredSide;

  useEffect(() => {
    if (!controller) {
      return;
    }

    if (isSkyxNewRoute(controller.getGameSlug?.() ?? '')) {
      return;
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.code !== 'Space' && event.key !== ' ') {
        return;
      }
      if (event.repeat || event.altKey || event.ctrlKey || event.metaKey) {
        return;
      }
      if (isEditableTarget(event.target)) {
        return;
      }

      const activeController = controllerRef.current;
      if (!activeController) {
        return;
      }

      if (isSkyxNewRoute(activeController.getGameSlug?.() ?? '')) {
        return;
      }

      const useGameStore = resolveGameStoreHook(activeController as any);
      const handled = performBettingSpacebarAction(
        activeController,
        useGameStore.getState() as BettingHotkeyStoreState,
        preferredSideRef.current
      );

      if (handled) {
        event.preventDefault();
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [controller]);
}
